// components/RegisterVendor.js — minimal signup; full store setup happens in the vendor dashboard after login.
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2, XCircle, Circle } from "lucide-react";
import { isValidEmail } from "../../utils/validators";
import { buildApiUrl } from "../../services/apiClientService";

// ── Password strength rules (mirrors backend passwordPolicy.js) ─────────────
function getPasswordChecks(pwd) {
  return [
    { id: "len",    label: "At least 8 characters",           pass: pwd.length >= 8 },
    { id: "letter", label: "At least one letter (a–z / A–Z)", pass: /[a-zA-Z]/.test(pwd) },
    { id: "num",    label: "At least one number (0–9)",       pass: /[0-9]/.test(pwd) },
    { id: "upper",  label: "At least one uppercase letter",   pass: /[A-Z]/.test(pwd) },
    { id: "spec",   label: "At least one special character",  pass: /[^a-zA-Z0-9]/.test(pwd) },
  ];
}

function strengthScore(checks) {
  return checks.filter((c) => c.pass).length;
}

const STRENGTH_META = [
  null,
  { label: "Very weak",  bar: "w-1/5",  color: "bg-red-500",    text: "text-red-600"    },
  { label: "Weak",       bar: "w-2/5",  color: "bg-orange-400", text: "text-orange-500" },
  { label: "Fair",       bar: "w-3/5",  color: "bg-yellow-400", text: "text-yellow-600" },
  { label: "Strong",     bar: "w-4/5",  color: "bg-emerald-400",text: "text-emerald-600"},
  { label: "Very strong",bar: "w-full", color: "bg-emerald-500",text: "text-emerald-700"},
];

const RegisterVendor = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    storeName: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);

  const checks = useMemo(() => getPasswordChecks(form.password), [form.password]);
  const score   = useMemo(() => strengthScore(checks), [checks]);
  const meta    = score > 0 ? STRENGTH_META[score] : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "password") setPwTouched(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // All 3 minimum rules must pass (mirrors backend)
    const required = checks.filter((c) => ["len", "letter", "num"].includes(c.id));
    if (required.some((c) => !c.pass)) {
      alert("Password does not meet the minimum requirements.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match.");
      return;
    }
    if (!isValidEmail(form.email)) {
      alert("Please enter a valid email address.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("password", form.password);
      fd.append("phone", form.phone);
      fd.append("storeName", form.storeName);

      const res = await fetch(buildApiUrl("/api/vendors/register"), {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || data.message || "Registration failed");
        return;
      }
      alert(
        data.message ||
          "Account created. Log in and complete your store profile in the dashboard.",
      );
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    }
  };

  const confirmMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;
  const confirmMismatch =
    form.confirmPassword.length > 0 && form.password !== form.confirmPassword;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl bg-white shadow-2xl rounded-xl flex overflow-hidden">
        {/* Left panel */}
        <div className="w-1/2 bg-rose-600 text-white p-10 space-y-6 hidden md:flex md:flex-col md:justify-center">
          <h2 className="text-4xl font-bold">Become a Vendor</h2>
          <p className="text-rose-100">
            Create an account with the basics. After you log in, you&apos;ll set up your store URL,
            address, delivery or pickup options, and branding — everything admins need to review
            before activating your shop.
          </p>
          <ul className="space-y-2 text-rose-50">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-white/80 shrink-0" />
              Quick signup — only essentials here
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-white/80 shrink-0" />
              Complete your store profile in the vendor dashboard
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-white/80 shrink-0" />
              Admin activates your account after review
            </li>
          </ul>
        </div>

        {/* Right panel — form */}
        <div className="w-full md:w-1/2 p-8 overflow-y-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-1">Vendor registration</h2>
          <p className="text-sm text-gray-500 mb-6">
            You&apos;ll add your public store link, address, and fulfilment details after you sign in.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <input
              className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400"
              type="text"
              name="name"
              placeholder="Your full name"
              value={form.name}
              onChange={handleChange}
              required
            />

            {/* Email */}
            <input
              className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400"
              type="email"
              name="email"
              placeholder="Email address"
              value={form.email}
              onChange={handleChange}
              required
            />

            {/* Password + strength */}
            <div className="space-y-2">
              <div className="relative">
                <input
                  className="w-full p-3 pr-11 border rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create a password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {/* Strength bar */}
              {pwTouched && form.password.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${meta?.color ?? ""} ${meta?.bar ?? "w-0"}`}
                      />
                    </div>
                    {meta && (
                      <span className={`text-xs font-semibold whitespace-nowrap ${meta.text}`}>
                        {meta.label}
                      </span>
                    )}
                  </div>

                  {/* Checklist */}
                  <ul className="space-y-0.5">
                    {checks.map((c) => (
                      <li key={c.id} className="flex items-center gap-1.5 text-xs">
                        {c.pass ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                        ) : (
                          <Circle className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                        )}
                        <span className={c.pass ? "text-emerald-700" : "text-gray-500"}>
                          {c.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div className="space-y-1">
              <div className="relative">
                <input
                  className={`w-full p-3 pr-11 border rounded-xl focus:outline-none focus:ring-2 ${
                    confirmMismatch
                      ? "border-red-400 focus:ring-red-300"
                      : confirmMatch
                      ? "border-emerald-400 focus:ring-emerald-300"
                      : "focus:ring-rose-400"
                  }`}
                  type={showConfirm ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirm((s) => !s)}
                  tabIndex={-1}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {confirmMismatch && (
                <p className="flex items-center gap-1 text-xs text-red-600">
                  <XCircle className="h-3.5 w-3.5 shrink-0" /> Passwords do not match
                </p>
              )}
              {confirmMatch && (
                <p className="flex items-center gap-1 text-xs text-emerald-600">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Passwords match
                </p>
              )}
            </div>

            {/* Phone */}
            <input
              className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400"
              type="text"
              name="phone"
              placeholder="Phone number"
              value={form.phone}
              onChange={handleChange}
              required
            />

            {/* Store name */}
            <input
              className="w-full p-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400"
              type="text"
              name="storeName"
              placeholder="Store / business name"
              value={form.storeName}
              onChange={handleChange}
              required
            />

            <button
              className="w-full p-3 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition disabled:opacity-50"
              type="submit"
            >
              Create account
            </button>
          </form>

          <p className="text-sm text-center mt-4 text-gray-600">
            Already registered?{" "}
            <Link to="/vendor/login" className="text-rose-600 font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterVendor;
